import Grid from '@mui/material/Grid';
import {
  AccountType,
  getV1CompaniesCompanyIdProjectsId,
  ProjectState,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import useAccount from '~hooks/useAccount/useAccount';
import { useDepartment } from '~hooks/useDepartment/useDepartment';
import { Phase, Project } from '~hooks/useProjects/models';
import useProjects from '~hooks/useProjects/useProjects';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import type { PhaseOptions } from './PhaseDrawer/types';
import { PhaseSelection } from './PhaseSelection';

interface ProjectDetailsProps<TFieldValues extends FieldValues = FieldValues> {
  companyId?: string | null;
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  readOnly?: boolean;
  source?: 'order-form';
  tomlinsonOrderFormProjectFirstFeatureFlagEnabled?: boolean;
  tomlinsonOrderModalFeatureFlagEnabled?: boolean;
}

function ProjectDetailsComponent<TFieldValues extends FieldValues = FieldValues>({
  companyId,
  control,
  errors,
  readOnly = false,
  tomlinsonOrderFormProjectFirstFeatureFlagEnabled,
  tomlinsonOrderModalFeatureFlagEnabled,
  source,
}: ProjectDetailsProps<TFieldValues>) {
  const { companyAssetsStore, userStore } = useStores();
  const effectiveCompanyId = getEffectiveUserCompanyId(userStore);
  const { getProjectsByCompanyIdTypeahead } = useProjects();
  const { getAccountsByCompanyIdTypeahead } = useAccount();
  const { loadDepartmentOptions } = useDepartment();

  const departmentOptions = companyAssetsStore.departments;
  const watchAccount = useWatch({
    control: control as Control<FieldValues>,
    name: 'account',
  }) as { id?: string } | null;
  const watchProject = useWatch({
    control: control as Control<FieldValues>,
    name: 'project',
  }) as { id?: string } | null;
  const watchPhases = useWatch({
    control: control as Control<FieldValues>,
    name: 'phases',
  }) as Phase[] | undefined;
  const watchOrder = useWatch({
    control: control as Control<FieldValues>,
    name: 'id',
  }) as string | undefined;
  const [loaded, setLoaded] = useState(false);
  const [localPhaseNameOptions, setLocalPhaseNameOptions] = useState<PhaseOptions[]>([]);
  const [selectedPhase, setSelectedPhase] = useState<PhaseOptions | null>(null);
  const allowCreate = useMemo(() => {
    return !watchProject?.id || Boolean(watchOrder);
  }, [watchProject?.id, watchOrder]);
  const { setValue, getValues } = useFormContext();

  // PhaseSelection has its own internal useForm; the user's pick reaches the
  // parent form only through this callback. Without it, `phase` stays at its
  // default empty value and `phases[]` carries no `primary: true` marker.
  const handlePhaseChange = useCallback(
    (selection: PhaseOptions | null) => {
      setValue(
        'phase',
        selection
          ? { id: selection.id ?? '', name: selection.name, code: selection.code ?? '' }
          : { id: '', name: '', code: '' },
      );
      const currentPhases = (getValues('phases') ?? []) as PhaseOptions[];
      setValue(
        'phases',
        currentPhases.map((p) => ({
          ...p,
          primary: Boolean(
            selection && p.name === selection.name && p.code === selection.code,
          ),
        })),
      );
    },
    [setValue, getValues],
  );

  useEffect(() => {
    if (companyId && !readOnly) {
      void loadDepartmentOptions(companyId);
    }
  }, [companyId, readOnly, loadDepartmentOptions]);

  const fetchAccountsByCompanyId = async (additionalProps = {}) => {
    if (!companyId || readOnly) {
      return [];
    }
    return await getAccountsByCompanyIdTypeahead({
      companyId,
      accountTypes: [AccountType.CUSTOMER],
      ...additionalProps,
    });
  };

  const fetchProjectsByCompanyId = async (additionalProps = {}) => {
    if (!companyId || readOnly) {
      return [];
    }

    return await getProjectsByCompanyIdTypeahead({
      companyId: companyId,
      states: [ProjectState.ACTIVE],
      accountId: watchAccount?.id,
      ...additionalProps,
    });
  };

  useEffect(() => {
    if (!loaded) {
      if (watchPhases) {
        setLocalPhaseNameOptions(
          watchPhases.map((item: Phase) => ({
            id: item.id,
            name: item.name,
            code: item.code,
            primary: item.primary,
          })),
        );

        const primary = watchPhases.find((item: Phase) => item.primary);
        if (primary) {
          setSelectedPhase({
            id: primary.id,
            name: primary.name,
            code: primary.code,
            primary: primary.primary,
          });
        }
      }
      setLoaded(true);
    }
  }, [JSON.stringify(watchPhases), loaded]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      if (watchProject?.id && !watchOrder && loaded) {
        try {
          const response = await getV1CompaniesCompanyIdProjectsId({
            path: { 'company-id': effectiveCompanyId, id: watchProject.id },
            signal: controller.signal,
          });
          if (controller.signal.aborted) return;
          const project = Project.parse(response.data.data);

          const items =
            project.phases.map((item) => ({
              id: item.id,
              name: item.name,
              code: item.code,
              primary: item.primary,
            })) || [];

          setLocalPhaseNameOptions(items);
          setSelectedPhase(null);
        } catch (error) {
          if (controller.signal.aborted) return;

          connection.handleRequestError(
            error,
            t('error_messages.projects.failed_to_fetch_project'),
          );
        }
      }
    };

    void fetchData();

    return () => controller.abort();
  }, [watchProject?.id, effectiveCompanyId, loaded, watchOrder]);

  const accountField = (
    <Grid size={{ xs: 6 }}>
      <AutocompleteAsyncFormField
        control={control}
        disabled={!companyId || readOnly}
        errors={errors}
        name={'account'}
        label={`${t('dispatch.order.customer_account')}`}
        isRequired={true}
        getValue={(item) => item.id}
        getLabel={(item) => item.name || ''}
        asyncCallback={fetchAccountsByCompanyId}
        onSelect={(option) => {
          setValue('project', null);
          setLocalPhaseNameOptions([]);
          setSelectedPhase(null);
          return option;
        }}
      />
    </Grid>
  );

  const projectField = (
    <Grid size={{ xs: 6 }}>
      <AutocompleteAsyncFormField
        control={control}
        errors={errors}
        disabled={!companyId || readOnly}
        name={'project'}
        label={`${t('order.form.associated_project_name')}`}
        clearable={true}
        onClear={() => {
          // because AutocompleteAsyncFormField won't clear the value if onClear is set.
          setValue('project', null);
          if (!watchOrder) {
            // only clear phase options on create, not on update.
            setLocalPhaseNameOptions([]);
            setSelectedPhase(null);
          }
        }}
        getValue={(item) => item.id}
        getLabel={(item) =>
          item.externalId ? `${item.name} - ${item.externalId}` : item.name
        }
        asyncCallback={fetchProjectsByCompanyId}
      />
    </Grid>
  );

  return (
    <>
      {tomlinsonOrderFormProjectFirstFeatureFlagEnabled ? (
        <>
          {projectField}
          {accountField}
        </>
      ) : (
        <>
          {accountField}
          {projectField}
        </>
      )}

      <Grid size={{ xs: 6 }}>
        {tomlinsonOrderModalFeatureFlagEnabled ? null : (
          <TextFormField
            control={control}
            errors={errors}
            disabled={true}
            name="projectExternalId"
            label={`${t('form_fields.project_id')}`}
            isRequired={false}
          />
        )}
      </Grid>
      <Grid size={{ xs: 6 }}>
        <AutocompleteFormField
          control={control}
          name="department"
          groupBy={(item) => item?.typeName}
          errors={errors}
          sx={{ mr: 2 }}
          list={departmentOptions}
          label={`${t('project.form.department')}`}
          getValue={(item) => item.id}
          getLabel={(item) => item.name || ''}
          clearable={true}
          disabled={readOnly}
        />
      </Grid>
      {!readOnly && (
        <PhaseSelection
          phaseOptions={localPhaseNameOptions}
          canEdit={allowCreate}
          selectedPhase={selectedPhase}
          onChange={handlePhaseChange}
          onPhaseOptionsChange={(options) => {
            setValue('phases', options);
          }}
          isFormSubmitted={
            source === 'order-form' ? control._formState.isSubmitted : undefined
          }
        />
      )}
    </>
  );
}

// observer drops the generic parameter, so the export keeps the component's own signature
export const ProjectDetails = observer(
  ProjectDetailsComponent,
) as typeof ProjectDetailsComponent;
